/**
 * Rotas da PWA.
 *
 * As cinco posições da barra (Home · Financeiro · + · Roteiro · Mais) vêm de
 * `@nv/core`; a barra só aparece nas raízes. A pilha real é o histórico do próprio
 * navegador — `roteiro/:dia/:atividade` volta para o dia, não para a Home,
 * sem código de pilha nenhum. Deep links deixam de existir como problema:
 * são URLs.
 */
import { isRootRoute, TAB_LABELS, TAB_ROUTES, TABS } from '@nv/core';
import { useEffect, useState } from 'react';
import { NavLink, Outlet, Route, Routes, useLocation } from 'react-router-dom';

import { useAberturaDoApp, useAreaPorRota } from '../telemetry/useTelemetria';

import { RequireAuth } from '../auth/RequireAuth';
import { SairSheet } from '../auth/SairSheet';
import { PedidoDeAlertas } from '../features/alertas/PedidoDeAlertas';
import { ConviteScreen } from '../features/auth/ConviteScreen';
import { CriarContaScreen } from '../features/auth/CriarContaScreen';
import { LoginScreen } from '../features/auth/LoginScreen';
import { SenhaNovaScreen } from '../features/auth/SenhaNovaScreen';
import { SenhaScreen } from '../features/auth/SenhaScreen';
import { SplashScreen } from '../features/auth/SplashScreen';
import {
  DespesaRoute,
  EditarDespesaRoute,
  FecharRoute,
  FinanceiroRoute,
  NovaDespesaRoute,
} from '../features/financeiro/rotas';
import {
  ConvitesRoute,
  CriarFamiliaRoute,
  GrupoRoute,
  NegadoRoute,
  PermissoesRoute,
  TransferirRoute,
} from '../features/grupo/rotas';
import { CriarSheet } from '../features/criar/CriarSheet';
import { MaisScreen } from '../features/mais/MaisScreen';
import { BarraVoltar } from '../ui/BarraVoltar';
import { Toast } from '../ui/Toast';
import { BagagemScreen } from '../features/bagagem/BagagemScreen';
import { EnquetesScreen } from '../features/enquetes/EnquetesScreen';
import { RankingScreen } from '../features/ranking/RankingScreen';
import { PassaporteScreen } from '../features/carimbos/PassaporteScreen';
import { CarroScreen } from '../features/carro/CarroScreen';
import { SugestoesScreen } from '../features/sugestoes/SugestoesScreen';
import { MapaScreen } from '../features/mapa/MapaScreen';
import { AlbumScreen } from '../features/memorias/AlbumScreen';
import { EnviarScreen } from '../features/memorias/EnviarScreen';
import { FotoScreen } from '../features/memorias/FotoScreen';
import { MemoriasScreen } from '../features/memorias/MemoriasScreen';
import { useRetomarCompartilhamento } from '../features/mapa/useRetomarCompartilhamento';
import { PerfilScreen } from '../features/perfil/PerfilScreen';
import { DemoPanel } from '../features/home/DemoPanel';
import { showDemoPanel } from '../demo/visibility';
import { HomeScreen } from '../features/home/HomeScreen';
import { AtividadeFormScreen } from '../features/roteiro/AtividadeFormScreen';
import { AtividadeScreen } from '../features/roteiro/AtividadeScreen';
import { DiaScreen } from '../features/roteiro/DiaScreen';
import { HistoricoScreen } from '../features/roteiro/HistoricoScreen';
import { RoteiroScreen } from '../features/roteiro/RoteiroScreen';
import { ConfiguracoesViagemScreen } from '../features/viagens/ConfiguracoesViagemScreen';
import { CriarViagemScreen } from '../features/viagens/CriarViagemScreen';
import { EmergenciaScreen } from '../features/emergencia/EmergenciaScreen';
import { useFila } from '../features/memorias/fila';
import { MinhasViagensScreen } from '../features/viagens/MinhasViagensScreen';
import { useTrip } from '../trip/TripContext';
import { ViagemAtivaGate } from '../trip/ViagemAtivaGate';
import { ConflictSheet } from '../ui/ConflictSheet';
import { useItinerary } from '../features/roteiro/store';
import { Icon } from '../ui/icons';
import { NovaVersao } from '../ui/NovaVersao';
import { SyncBadge } from '../ui/SyncBadge';
import { InstallHint } from '../install/InstallHint';

function Screen({ title }: { title: string }) {
  return (
    <section className="screen">
      <h1>{title}</h1>
      <div className="card">
        <p>
          Casca da tela. O conteúdo vem do protótipo aprovado, reproduzido com
          os mesmos tokens.
        </p>
      </div>
    </section>
  );
}

/**
 * O toast de confirmação vive no Shell, não na tela: quem cria uma atividade
 * a partir da Home volta para a Home, e o toast precisa aparecer ali — e não
 * só quando o caminho de volta passa pelo dia.
 */
function ToastDoRoteiro() {
  const toast = useItinerary((s) => s.toast);
  if (!toast) return null;
  return <Toast texto={toast} />;
}

function Shell() {
  const { pathname } = useLocation();
  const { trip } = useTrip();
  // Telemetria mínima do beta (rodada 2B2): abertura do app e área aberta —
  // só aqui, com identidade e viagem; nunca antes do login.
  useAberturaDoApp(trip.id);
  useAreaPorRota(trip.id);
  // Compartilhamento de posição continua em qualquer aba, enquanto o app
  // estiver aberto — a escolha foi feita no Mapa, o envio é do app.
  useRetomarCompartilhamento();
  // Fotos que ficaram na fila de outra abertura sobem sozinhas.
  useEffect(() => {
    void useFila.getState().restaurar();
  }, []);

  // A barra inferior só existe nas quatro raízes; toda tela interna ganha a
  // seta de voltar no topo e a tela inteira (decisão do PO, 17/09/2026).
  const raiz = isRootRoute(pathname);
  const [criando, setCriando] = useState(false);

  return (
    <div className={raiz ? 'app-shell' : 'app-shell app-shell--interna'}>
      <InstallHint />
      <NovaVersao />
      <SyncBadge />
      {!raiz && <BarraVoltar />}
      <main className="app-scroll" key={pathname}>
        <Outlet />
      </main>
      {raiz && (
        <nav className="bottom-nav" aria-label="Navegação principal" style={{ ['--tab-i' as string]: Math.max(0, TABS.findIndex((t) => t !== 'criar' && TAB_ROUTES[t] === pathname)) }}>
          {/* Um traço só, que desliza até a aba ativa (refino final, 220ms). */}
          {TABS.some((t) => t !== 'criar' && TAB_ROUTES[t] === pathname) && <span className="bottom-nav__indicador" aria-hidden="true" />}
          {TABS.map((tab) =>
            tab === 'criar' ? (
              <button key={tab} type="button" className="tab tab--criar" aria-label="Criar" aria-expanded={criando} data-testid="tab-criar" onClick={() => setCriando((v) => !v)}>
                <span className="tab__mais" aria-hidden="true">
                  <Icon name="criar" size={26} />
                </span>
              </button>
            ) : (
              <NavLink
                key={tab}
                to={TAB_ROUTES[tab]}
                end
                className={({ isActive }) => (isActive ? 'tab tab--active' : 'tab')}
              >
                <Icon name={tab === 'financeiro' ? 'dinheiro' : tab} />
                <span className="tab__label">{TAB_LABELS[tab]}</span>
              </NavLink>
            ),
          )}
        </nav>
      )}
      {criando && <CriarSheet onFechar={() => setCriando(false)} />}
      <ToastDoRoteiro />
      <ConflictSheet />
      <SairSheet />
      <PedidoDeAlertas />
      {showDemoPanel() && <DemoPanel />}
    </div>
  );
}

/**
 * As telas fora da sessão não têm bottom nav — são casca própria, como o
 * protótipo define. Por isso ficam FORA do `Shell`, e não escondendo a
 * navegação por dentro dele.
 */
export function AppRoutes() {
  return (
    <Routes>
      <Route path="/splash" element={<SplashScreen />} />
      <Route path="/entrar" element={<LoginScreen />} />
      <Route path="/senha" element={<SenhaScreen />} />
      <Route path="/senha/nova" element={<SenhaNovaScreen />} />
      <Route path="/convite" element={<ConviteScreen />} />
      <Route path="/criar-conta" element={<CriarContaScreen />} />

      {/* Fora do `Shell` e fora da viagem ativa: quem acabou de criar conta
          ainda não tem viagem nenhuma, e essas duas telas são justamente o
          caminho para ter. */}
      <Route
        path="/viagens"
        element={
          <RequireAuth>
            <MinhasViagensScreen />
          </RequireAuth>
        }
      />
      <Route
        path="/viagens/nova"
        element={
          <RequireAuth>
            <CriarViagemScreen />
          </RequireAuth>
        }
      />

      <Route
        element={
          <RequireAuth>
            {/* Qual viagem? A escolhida — e, sem rede, a última escolhida. */}
            <ViagemAtivaGate>
              <Shell />
            </ViagemAtivaGate>
          </RequireAuth>
        }
      >
        <Route path="/" element={<HomeScreen />} />
        <Route path="/roteiro" element={<RoteiroScreen />} />
        <Route path="/roteiro/:dia" element={<DiaScreen />} />
        {/* Rotas fixas antes da paramétrica: senão "historico" e "nova"
            seriam lidos como id de atividade. */}
        <Route path="/roteiro/:dia/historico" element={<HistoricoScreen />} />
        <Route path="/roteiro/:dia/nova" element={<AtividadeFormScreen mode="nova" />} />
        <Route path="/roteiro/:dia/:atividade" element={<AtividadeScreen />} />
        <Route
          path="/roteiro/:dia/:atividade/editar"
          element={<AtividadeFormScreen mode="editar" />}
        />
        {/* Rotas fixas antes da paramétrica, pelo mesmo motivo do roteiro. */}
        <Route path="/financeiro" element={<FinanceiroRoute />} />
        <Route path="/financeiro/nova" element={<NovaDespesaRoute />} />
        <Route path="/financeiro/fechar" element={<FecharRoute />} />
        <Route path="/financeiro/:despesa/editar" element={<EditarDespesaRoute />} />
        <Route path="/financeiro/:despesa" element={<DespesaRoute />} />
        <Route path="/grupo" element={<GrupoRoute />} />
        <Route path="/grupo/criar-familia" element={<CriarFamiliaRoute />} />
        <Route path="/grupo/convites" element={<ConvitesRoute />} />
        <Route path="/grupo/transferir" element={<TransferirRoute />} />
        <Route path="/grupo/:membro/permissoes" element={<PermissoesRoute />} />
        <Route path="/mapa" element={<MapaScreen />} />
        <Route path="/bagagem" element={<BagagemScreen />} />
        <Route path="/enquetes" element={<EnquetesScreen />} />
        <Route path="/ranking" element={<RankingScreen />} />
        <Route path="/carro" element={<CarroScreen />} />
        <Route path="/sugestoes" element={<SugestoesScreen />} />
        <Route path="/passaporte" element={<PassaporteScreen />} />
        <Route path="/memorias" element={<MemoriasScreen />} />
        <Route path="/memorias/enviar" element={<EnviarScreen />} />
        <Route path="/memorias/:dia" element={<AlbumScreen />} />
        <Route path="/memorias/:dia/:foto" element={<FotoScreen />} />
        <Route path="/mais" element={<MaisScreen />} />
        <Route path="/perfil" element={<PerfilScreen />} />
        {/* Configurações são DA viagem aberta: ficam dentro do gate. */}
        <Route path="/viagem/configuracoes" element={<ConfiguracoesViagemScreen />} />
        <Route path="/emergencia" element={<EmergenciaScreen />} />
        {/* Destino dos botões com 🔒: precisa dos membros da viagem para dizer
            quem pode liberar — por isso dentro do gate. */}
        <Route path="/negado" element={<NegadoRoute />} />
        <Route path="*" element={<Screen title="Não encontrado" />} />
      </Route>
    </Routes>
  );
}
